/**
 * Compact game info bar (ScoreChips) + expandable detail dialog.
 * Shows player pill badges inline, full details on tap.
 */

import type {
  PlayerId,
  StoneFlickingState,
} from "@cloudnest/augmented-stone-flicking-engine";
import { countRemaining } from "@cloudnest/stone-flicking-core-engine/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScoreChips } from "./components/ScoreChips";

interface GameInfoProps {
  readonly gameState: StoneFlickingState;
  readonly isAnimating: boolean;
  readonly readyPlayers: ReadonlyArray<string>;
  readonly onSurrender: () => void;
  readonly onLeaveGame: () => void;
}

const PLAYER_COLORS: ReadonlyArray<string> = [
  "var(--cn-asf-stone-p1)",
  "var(--cn-asf-stone-p2)",
  "var(--cn-asf-stone-p3)",
  "var(--cn-asf-stone-p4)",
];

const PLAYER_BG_COLORS: ReadonlyArray<string> = [
  "var(--cn-asf-stone-p1-bg)",
  "var(--cn-asf-stone-p2-bg)",
  "var(--cn-asf-stone-p3-bg)",
  "var(--cn-asf-stone-p4-bg)",
];

export function GameInfo({
  gameState,
  isAnimating,
  readyPlayers,
  onSurrender,
  onLeaveGame,
}: GameInfoProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const participants = gameState.coreState.participants;
  const eliminated = gameState.eliminatedPlayers;

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Focus trap: focus dialog when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  const pieceCounts = participants.map((p, idx) => {
    const count = countRemaining(gameState.coreState.entities, p.id);
    const isEliminated = eliminated.includes(p.id as PlayerId);
    const isReady = readyPlayers.includes(p.id);
    const color =
      PLAYER_COLORS[idx % PLAYER_COLORS.length] ?? "var(--cn-asf-text)";
    const bgColor =
      PLAYER_BG_COLORS[idx % PLAYER_BG_COLORS.length] ??
      "var(--cn-asf-surface-secondary)";
    return {
      pid: p.id,
      label: p.id,
      count,
      isEliminated,
      isReady,
      color,
      bgColor,
    };
  });

  return (
    <>
      {/* Compact bar — ScoreChips with expand trigger */}
      <button
        type="button"
        onClick={toggleOpen}
        data-testid="game-info-bar"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 12px",
          minHeight: "36px",
          backgroundColor: "var(--cn-asf-surface)",
          borderRadius: "20px",
          border: "1px solid var(--cn-asf-surface-border)",
          boxShadow: "var(--cn-shadow-sm, 0 1px 3px rgba(0,0,0,0.08))",
          cursor: "pointer",
        }}
      >
        <ScoreChips
          players={pieceCounts.map((p) => ({
            playerId: p.pid,
            label: p.label,
            count: p.count,
            color: p.color,
            bgColor: p.bgColor,
            isReady: p.isReady,
            isEliminated: p.isEliminated,
          }))}
        />
        <span
          style={{ color: "var(--cn-asf-text-muted)", fontSize: "10px" }}
          aria-hidden="true"
        >
          ▼
        </span>
      </button>

      {/* Detail dialog (overlay) */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="게임 정보"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            animation: "asf-fade-in 0.2s ease-out",
          }}
          onClick={closeDialog}
          onKeyDown={(e) => {
            if (e.key === "Escape") closeDialog();
          }}
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            style={{
              backgroundColor: "var(--cn-asf-surface)",
              border: "1px solid var(--cn-asf-surface-border)",
              borderRadius: "16px",
              padding: "20px",
              minWidth: "240px",
              maxWidth: "320px",
              boxShadow: "var(--cn-shadow-lg, 0 8px 32px rgba(0,0,0,0.2))",
              animation: "asf-slide-up 0.25s ease-out",
              outline: "none",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeDialog();
              else e.stopPropagation();
            }}
          >
            <div
              style={{
                fontSize: "15px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "var(--cn-asf-text)",
              }}
            >
              게임 정보
            </div>

            {pieceCounts.map((p) => (
              <div
                key={p.pid}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  fontSize: "14px",
                  opacity: p.isEliminated ? 0.4 : 1,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {/* Player color avatar */}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: p.color,
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    {p.label.replace("P", "")}
                  </span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "var(--cn-asf-text)",
                    }}
                  >
                    {p.label}
                    {p.isReady && (
                      <span
                        style={{
                          color: "var(--cn-asf-ready)",
                          marginLeft: "4px",
                        }}
                      >
                        ✓
                      </span>
                    )}
                    {p.isEliminated && (
                      <span
                        style={{
                          color: "var(--cn-asf-text-muted)",
                          marginLeft: "4px",
                          fontSize: "11px",
                        }}
                      >
                        탈락
                      </span>
                    )}
                  </span>
                </div>
                <span
                  data-testid={`${p.pid.toLowerCase()}-piece-count`}
                  style={{
                    fontWeight: "bold",
                    color: "var(--cn-asf-text)",
                  }}
                >
                  {p.count}개
                </span>
              </div>
            ))}

            <div
              style={{
                borderTop: "1px solid var(--cn-asf-surface-border)",
                marginTop: "12px",
                paddingTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <button
                data-testid="resign-btn"
                type="button"
                style={{
                  padding: "10px 16px",
                  border: "1px solid var(--cn-asf-surface-border)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  width: "100%",
                  backgroundColor: "var(--cn-asf-surface-secondary)",
                  color: "var(--cn-asf-text)",
                  minHeight: "44px",
                }}
                onClick={() => {
                  onSurrender();
                  closeDialog();
                }}
                disabled={isAnimating || gameState.phase !== "PLAYING"}
              >
                기권
              </button>
              <button
                data-testid="leave-game-btn"
                type="button"
                style={{
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  width: "100%",
                  backgroundColor: "var(--cn-asf-stone-p1)",
                  color: "#fff",
                  minHeight: "44px",
                }}
                onClick={() => {
                  onLeaveGame();
                  closeDialog();
                }}
              >
                나가기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
